import { requireOrgAdminBeforeLoad } from '../../lib/org-admin-guard';
import { createFileRoute } from '@tanstack/react-router';

/**
 * X6 (2026-05-21): `?path=<canonical>` deep-link support so the
 * "Open in Explorer" action in the card slide-over can jump straight
 * to a card's workspace folder. The validator narrows + sanity-checks
 * the input so we don't pass a hostile string through to the tree.
 *
 * Constraints kept inline so the eager stub does NOT pull in zod (and
 * the form-vendor chunk) on initial load.
 */
function validateFilesSearch(search: Record<string, unknown>): { path?: string } {
  const p = search?.path;
  if (typeof p !== 'string') return {};
  // A cheap pre-filter, not the root check: the router cannot know the
  // gateway's Explorer root synchronously (it is `/srv/chaos` in production
  // and `/srv/chaos-stg` on the staging stand), so this only stops a
  // deep-link that is not even under `/srv/chaos…` from reaching the tree.
  // The page then compares against the root the gateway reports
  // (isUnderExplorerRoot in files.lazy.tsx), and the backend rejects
  // anything outside it regardless.
  if (!p.startsWith('/srv/chaos')) return {};
  // Refuse obvious traversal attempts.
  if (p.includes('//') || p.split('/').some((seg) => seg === '..' || seg === '.')) {
    return {};
  }
  if (p.includes('\0')) return {};
  // Cap length so a runaway query doesn't blow out the URL bar (a
  // typical /workspace path is ~150 chars).
  if (p.length > 1024) return {};
  return { path: p };
}

export const Route = createFileRoute('/_authenticated/files')({
  beforeLoad: requireOrgAdminBeforeLoad,
  validateSearch: validateFilesSearch,
}).lazy(() => import('./files.lazy').then((d) => d.Route));
